import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { RefreshCw, Package, Factory, Download, CheckSquare, Mail, Loader2, Calculator, ShieldAlert, FileSpreadsheet, Percent, BookOpen, AlertTriangle } from 'lucide-react';
import * as XLSX from 'xlsx';
import CostSheetTester from './CostSheetTester';
import CpscComplianceWorklist from './CpscComplianceWorklist';
import WorklistTable from './carbon/WorklistTable';
import { useCpscBadges, CpscBadgeCell, isRowAttention, CpscOverrideModal } from './CpscBadges';
// CHA-276 — Carbon pass (Modules concept, Orders table anatomy). Presentation
// only: same getProductionData/report callables, same filters/sort/export and
// the same numbers. CpscComplianceWorklist + CostSheetTester render unchanged.
import { SkeletonRows, ErrorBanner, EmptyGoodNews } from './shell/carbonStates';

// Carbon class shorthands (chrome only)
const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border';
const MONO = 'font-plex-mono text-[11.5px]';
const chipCls = (on) => `px-2.5 py-1 font-plex-mono text-[10.5px] tracking-[0.04em] uppercase font-medium border ${
  on ? 'bg-carbon-ink text-carbon-white border-carbon-ink' : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
}`;

// Detail-table columns (label + row field + sort type). Drives the sortable header + client-side filter/sort.
// Order MUST match the <td> order in the detail row below.
const DETAIL_COLUMNS = [
  { label: 'Company', field: 'company' }, { label: 'Division', field: 'division' },
  { label: 'Customer', field: 'customer' }, { label: 'PO', field: 'po' },
  { label: 'Style', field: 'style' }, { label: 'Style Desc', field: 'styleDesc' },
  { label: 'PO Mgr', field: 'poManager' }, { label: 'COO', field: 'coo' },
  { label: 'Ship Terms', field: 'shipTerms' }, { label: 'Stage', field: 'stage' },
  { label: 'Prod Type', field: 'prodType' }, { label: 'Warehouse', field: 'shipCode' },
  { label: 'Pay Terms', field: 'paymentTerms' }, { label: 'Office', field: 'office' },
  { label: 'LC Benef', field: 'lcBeneficiary' }, { label: 'Factory', field: 'factory' },
  { label: 'Sales', field: 'totalSales', type: 'number', align: 'right' }, { label: 'FOB', field: 'fob', type: 'number', align: 'right' },
  { label: 'Cost', field: 'totalCost', type: 'number', align: 'right' }, { label: 'Price', field: 'price', type: 'number', align: 'right' },
  { label: 'GP$', field: 'totalGp', type: 'number', align: 'right' }, { label: 'Qty', field: 'totalQty', type: 'number', align: 'right' },
  { label: 'Carton', field: 'cartonQty', type: 'number', align: 'right' }, { label: 'Freight', field: 'freight', type: 'number', align: 'right' },
  { label: 'Inl Frt', field: 'inlandFreight', type: 'number', align: 'right' }, { label: 'Tariff', field: 'tariff', type: 'number', align: 'right' },
  { label: 'Tar%', field: 'tariffPct', type: 'number', align: 'right' }, { label: 'Duty%', field: 'dutyPct', type: 'number', align: 'right' },
  { label: 'Duty', field: 'duty', type: 'number', align: 'right' }, { label: 'Roy%', field: 'royaltyPct', type: 'number', align: 'right' },
  { label: 'Roy$', field: 'royaltyAmt', type: 'number', align: 'right' },
  { label: 'Due Date', field: 'dueDate', type: 'date' }, { label: 'Ship Date', field: 'shipDate', type: 'date' },
];
const cmpDetail = (a, b, col, dir) => {
  const av = a[col.field], bv = b[col.field];
  let c;
  if (col.type === 'number') c = (Number(av) || 0) - (Number(bv) || 0);
  else if (col.type === 'date') c = (Date.parse(av) || 0) - (Date.parse(bv) || 0);
  else c = String(av ?? '').localeCompare(String(bv ?? ''));
  return c * dir;
};

// ============================================================================
// PRODUCTION_2 DASHBOARD - Open Production & Received Production
// ============================================================================
export default function Production2Dashboard({ userProfile } = {}) {
  // Tab state
  const [activeTab, setActiveTab] = useState('open'); // 'open', 'received', or 'verification'
  // Sub-tab for verification
  const [verificationTab, setVerificationTab] = useState('issues'); // CHA-291 — the Issues dashboard is the landing view

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filter state
  const [company, setCompany] = useState('All');
  const [stage, setStage] = useState('All');
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedFinline, setSelectedFinline] = useState(null);

  // CPSC status badges (CHA-179) — feature-flagged, additive. Resolves each visible style to a CPSC badge
  // + drives the shipped-without-cert URGENT highlight + per-style override. All logic lives in CpscBadges.
  const cpsc = useCpscBadges(userProfile, data?.detailData);
  const [overrideStyle, setOverrideStyle] = useState(null); // style whose override modal is open

  // Detail-table client-side sort + filter (CHA-179 UI ask). Operates on the loaded 500 rows.
  const [detailSort, setDetailSort] = useState({ key: null, dir: 'asc' });
  const [detailFilter, setDetailFilter] = useState('');
  const toggleDetailSort = useCallback((key) => setDetailSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);
  const displayRows = useMemo(() => {
    // Tag each source row with a stable id (source order) so React keys survive sort/filter — not the
    // post-sort array index, which reshuffles rows on every sort (CodeRabbit).
    let rows = (data?.detailData || []).map((r, i) => ({ ...r, _id: i }));
    const f = detailFilter.trim().toLowerCase();
    if (f) rows = rows.filter((r) => DETAIL_COLUMNS.some((c) => String(r[c.field] ?? '').toLowerCase().includes(f)));
    if (detailSort.key) {
      const col = DETAIL_COLUMNS.find((c) => c.field === detailSort.key);
      if (col) { const dir = detailSort.dir === 'asc' ? 1 : -1; rows = [...rows].sort((a, b) => cmpDetail(a, b, col, dir)); }
    }
    return rows;
  }, [data?.detailData, detailFilter, detailSort]);

  // Format currency in Millions with 2 decimal places
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0';
    const num = Number(value) || 0;
    if (num === 0) return '$0';
    const millions = num / 1000000;
    if (Math.abs(millions) >= 0.01) {
      return `$${millions.toFixed(2)}M`;
    }
    const thousands = num / 1000;
    if (Math.abs(thousands) >= 1) {
      return `$${thousands.toFixed(0)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format number with commas
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getProductionData = httpsCallable(functions, 'getProductionData', { timeout: 150000 });

      const result = await getProductionData({
        tab: activeTab,
        company: company === 'All' ? null : company,
        stage: stage === 'All' ? null : stage,
        selectedDivision: selectedDivision || null,
        selectedCustomer: selectedCustomer || null,
        selectedProgram: selectedProgram || null,
        selectedFinline: selectedFinline || null,
      });

      setData(result.data);
    } catch (err) {
      console.error('Error fetching production data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, company, stage, selectedDivision, selectedCustomer, selectedProgram, selectedFinline]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear all filters
  const clearFilters = () => {
    setCompany('All');
    setStage('All');
    setSelectedDivision(null);
    setSelectedCustomer(null);
    setSelectedProgram(null);
    setSelectedFinline(null);
  };

  // Check if any filters are active
  const hasActiveFilters = company !== 'All' || stage !== 'All' || selectedDivision !== null || selectedCustomer !== null || selectedProgram !== null || selectedFinline !== null;

  // Export details to Excel
  const handleExport = () => {
    if (!data?.detailData || data.detailData.length === 0) return;

    const exportData = data.detailData.map(row => ({
      'Company': row.company,
      'Division': row.division,
      'Customer': row.customer,
      'PO': row.po,
      'Style': row.style,
      'Style Desc': row.styleDesc,
      'PO Manager': row.poManager,
      'COO': row.coo,
      'Ship Terms': row.shipTerms,
      'Stage': row.stage,
      'Payment Terms': row.paymentTerms,
      'Office': row.office,
      'LC Beneficiary': row.lcBeneficiary,
      'Factory': row.factory,
      'Total Sales': row.totalSales,
      'FOB': row.fob,
      'Total Cost': row.totalCost,
      'Price': row.price,
      'Total GP': row.totalGp,
      'Total Qty': row.totalQty,
      'Carton Qty': row.cartonQty,
      'Freight': row.freight,
      'Inland Freight': row.inlandFreight,
      'Tariff': row.tariff,
      'Tariff %': row.tariffPct,
      'Duty %': row.dutyPct,
      'Duty': row.duty,
      'Royalty %': row.royaltyPct,
      'Royalty Amt': row.royaltyAmt,
      'Due Date': row.dueDate || '',
      'Ship Date': row.shipDate || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Production Details');
    XLSX.writeFile(wb, `Production_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Handle division click
  const handleDivisionClick = (divisionName) => {
    if (selectedDivision === divisionName) {
      setSelectedDivision(null); // Deselect if clicking same division
    } else {
      setSelectedDivision(divisionName);
    }
  };

  // Get totals for summary
  const getTotals = () => {
    if (!data?.divisionSummary) return { sales: 0, gp: 0, units: 0 };
    return data.divisionSummary.reduce((acc, d) => ({
      sales: acc.sales + (d.total_sales || 0),
      gp: acc.gp + (d.total_gp || 0),
      units: acc.units + (d.total_units || 0),
    }), { sales: 0, gp: 0, units: 0 });
  };

  const totals = getTotals();

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Header with Tabs */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">Production</h1>
          <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-0.5">
            {activeTab === 'open' && 'Open production (WIP)'}
            {activeTab === 'received' && 'Received production'}
            {activeTab === 'verification' && 'Data verification & quality checks'}
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Tab Navigation — Carbon underline tabs */}
      <div className="bg-carbon-white border border-carbon-border">
        <div className="flex border-b border-carbon-border overflow-x-auto">
          {[
            { id: 'open', label: 'Open Production', icon: Package },
            { id: 'received', label: 'Received Production', icon: Factory },
            { id: 'verification', label: 'Production Verification', icon: CheckSquare },
          ].map((tab) => {
            const Icon = tab.icon;
            const on = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); clearFilters(); }}
                className={`flex items-center gap-1.5 px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 whitespace-nowrap ${on
                  ? 'text-carbon-ink font-semibold border-carbon-blue-60'
                  : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
                  }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sub-tabs for Verification */}
        {activeTab === 'verification' && (
          <div className="flex border-b border-carbon-border bg-carbon-gray-10 overflow-x-auto">
            {[
              { id: 'issues', label: 'Issues', icon: AlertTriangle },         // CHA-291 — issues-only dashboard (default)
              { id: 'rules', label: 'Rules', icon: BookOpen },                // CHA-297 — the rule/check catalog
              // CHA-309 (operator 2026-07-15) — the per-check tabs (Costing Errors / Open Issued / Open
              // Shipped) are RETIRED as redundant: the Issues tab is the one issues surface.
              { id: 'tariff-rates', label: 'Tariff Rates', icon: Percent },   // CHA-295 — the rates OVERVIEW (issues land on the Issues tab)
              { id: 'cost-sheet-tester', label: 'Cost Sheet Tester', icon: Calculator },
              { id: 'cpsc-test', label: 'CPSC Docs', icon: ShieldAlert }
            ].map((tab) => {
              const Icon = tab.icon;
              const on = verificationTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setVerificationTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-[12.5px] -mb-px border-b-2 whitespace-nowrap ${on
                    ? 'text-carbon-ink font-semibold border-carbon-blue-60 bg-carbon-white'
                    : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
                    }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Filters + Summary Cards Row - Only for Open/Received tabs */}
        {(activeTab === 'open' || activeTab === 'received') && (
          <>
            <div className="p-4 border-b border-carbon-border">
              <div className="flex flex-wrap items-center gap-4">
                {/* Company Filter */}
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-1">Company</span>
                  {['All', 'IMG', 'IMG APPAREL', 'BWI', 'INNOVATION'].map((c) => (
                    <button key={c} onClick={() => setCompany(c)} className={chipCls(company === c)}>
                      {c}
                    </button>
                  ))}
                </div>

                {/* Divider */}
                <div className="h-5 w-px bg-carbon-border"></div>

                {/* Stage Filter */}
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-1">Stage</span>
                  {['All', 'ISSUED', 'SHIPPED'].map((s) => (
                    <button key={s} onClick={() => setStage(s)} className={chipCls(stage === s)}>
                      {s}
                    </button>
                  ))}
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-red-60 hover:bg-carbon-gray-10 px-2 py-1 ml-auto"
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* KPI strip — per-tile borders (never a gray-gap grid with odd tile counts) */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] border-l border-b border-carbon-border mt-4">
                {[
                  { label: 'Total Sales', value: formatCurrency(totals.sales), color: '#161616' },
                  { label: 'Total GP', value: formatCurrency(totals.gp), color: '#24A148' },
                  { label: 'Total Units', value: formatNumber(totals.units), color: '#0F62FE' },
                ].map((k) => (
                  <div key={k.label} className="bg-carbon-white px-4 py-3.5 border-r border-t border-carbon-border min-w-0">
                    <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{k.label}</div>
                    {loading ? (
                      <div className="h-6 w-20 bg-carbon-gray-10 animate-pulse mt-1.5" />
                    ) : (
                      <div className="font-plex-mono text-xl font-semibold mt-1.5" style={{ color: k.color }}>{k.value}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4">
              {loading ? (
                <SkeletonRows rows={6} />
              ) : error ? (
                <ErrorBanner what="Production data failed to load." staleNote={error} onRetry={fetchData} />
              ) : (
                <>
                  {/* Division Filter Row */}
                  <div className="mb-4">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-2">Division</span>
                      {data?.divisionSummary?.map((div) => {
                        // Shorten division names for display
                        const shortName = div.division_name.replace(/^\d+\./, '').replace(/\([^)]+\)/g, '').trim();
                        return (
                          <button
                            key={div.division_name}
                            onClick={() => handleDivisionClick(div.division_name)}
                            title={`${div.division_name}: ${formatCurrency(div.total_sales)}`}
                            className={`whitespace-nowrap ${chipCls(selectedDivision === div.division_name)}`}
                          >
                            {shortName}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Row of 3 Tables: Customer, Program, Finline */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {/* Customer Table */}
                    <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                      <div className="bg-carbon-gray-10 px-3 py-2 border-b border-carbon-border flex items-center justify-between">
                        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">By Customer</span>
                        {selectedCustomer && (
                          <span className="font-plex-mono text-[10px] font-medium border border-carbon-blue-60 text-carbon-blue-60 px-1.5 py-0.5">{selectedCustomer}</span>
                        )}
                      </div>
                      <div className="overflow-auto max-h-64">
                        <table className="w-full text-xs">
                          <thead className="bg-carbon-white sticky top-0">
                            <tr>
                              <th className={`${TH} text-left`}>Customer</th>
                              <th className={`${TH} text-right`}>Sales</th>
                              <th className={`${TH} text-right`}>Cost</th>
                              <th className={`${TH} text-right`}>GP$</th>
                              <th className={`${TH} text-right`}>GP%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.customerSummary?.map((row) => (
                              <tr
                                key={row.customer}
                                onClick={() => setSelectedCustomer(selectedCustomer === row.customer ? null : row.customer)}
                                className={`cursor-pointer border-b border-carbon-gray-10 ${selectedCustomer === row.customer
                                  ? 'bg-carbon-blue-10'
                                  : 'hover:bg-carbon-gray-10'
                                  }`}
                              >
                                <td className="px-2 py-1.5 text-[13px] text-carbon-ink truncate max-w-[100px]">{row.customer}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-ink`}>{formatCurrency(row.total_sales)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-50`}>{formatCurrency(row.total_cost)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO}`} style={{ color: '#24A148' }}>{formatCurrency(row.total_gp)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{row.gp_percent.toFixed(1)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Program Table */}
                    <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                      <div className="bg-carbon-gray-10 px-3 py-2 border-b border-carbon-border flex items-center justify-between">
                        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">By Program</span>
                        {selectedProgram && (
                          <span className="font-plex-mono text-[10px] font-medium border border-carbon-blue-60 text-carbon-blue-60 px-1.5 py-0.5">{selectedProgram}</span>
                        )}
                      </div>
                      <div className="overflow-auto max-h-64">
                        <table className="w-full text-xs">
                          <thead className="bg-carbon-white sticky top-0">
                            <tr>
                              <th className={`${TH} text-left`}>Program</th>
                              <th className={`${TH} text-right`}>Sales</th>
                              <th className={`${TH} text-right`}>Cost</th>
                              <th className={`${TH} text-right`}>GP$</th>
                              <th className={`${TH} text-right`}>GP%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.programSummary?.map((row) => (
                              <tr
                                key={row.program}
                                onClick={() => setSelectedProgram(selectedProgram === row.program ? null : row.program)}
                                className={`cursor-pointer border-b border-carbon-gray-10 ${selectedProgram === row.program
                                  ? 'bg-carbon-blue-10'
                                  : 'hover:bg-carbon-gray-10'
                                  }`}
                              >
                                <td className="px-2 py-1.5 text-[13px] text-carbon-ink truncate max-w-[100px]">{row.program}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-ink`}>{formatCurrency(row.total_sales)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-50`}>{formatCurrency(row.total_cost)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO}`} style={{ color: '#24A148' }}>{formatCurrency(row.total_gp)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{row.gp_percent.toFixed(1)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Finline Table */}
                    <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                      <div className="bg-carbon-gray-10 px-3 py-2 border-b border-carbon-border flex items-center justify-between">
                        <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">By Finline</span>
                        {selectedFinline && (
                          <span className="font-plex-mono text-[10px] font-medium border border-carbon-blue-60 text-carbon-blue-60 px-1.5 py-0.5">{selectedFinline}</span>
                        )}
                      </div>
                      <div className="overflow-auto max-h-64">
                        <table className="w-full text-xs">
                          <thead className="bg-carbon-white sticky top-0">
                            <tr>
                              <th className={`${TH} text-left`}>Finline</th>
                              <th className={`${TH} text-right`}>Sales</th>
                              <th className={`${TH} text-right`}>Cost</th>
                              <th className={`${TH} text-right`}>GP$</th>
                              <th className={`${TH} text-right`}>GP%</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.finlineSummary?.map((row) => (
                              <tr
                                key={row.finline}
                                onClick={() => setSelectedFinline(selectedFinline === row.finline ? null : row.finline)}
                                className={`cursor-pointer border-b border-carbon-gray-10 ${selectedFinline === row.finline
                                  ? 'bg-carbon-blue-10'
                                  : 'hover:bg-carbon-gray-10'
                                  }`}
                              >
                                <td className="px-2 py-1.5 text-[13px] text-carbon-ink truncate max-w-[100px]">{row.finline}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-ink`}>{formatCurrency(row.total_sales)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-50`}>{formatCurrency(row.total_cost)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO}`} style={{ color: '#24A148' }}>{formatCurrency(row.total_gp)}</td>
                                <td className={`px-2 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{row.gp_percent.toFixed(1)}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <CpscOverrideModal style={overrideStyle} ctx={cpsc} onClose={() => setOverrideStyle(null)} />

                  {/* Table 4: Detail Data */}
                  <div className="bg-carbon-white border border-carbon-border overflow-hidden">
                    <div className="bg-carbon-gray-10 px-3 py-2 border-b border-carbon-border flex items-center justify-between gap-2 flex-wrap">
                      <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">
                        Production Details <span className="text-carbon-gray-50">({displayRows.length}{detailFilter ? ` of ${data?.detailData?.length || 0}` : ''} records)</span>
                      </span>
                      <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={detailFilter}
                        onChange={(e) => setDetailFilter(e.target.value)}
                        placeholder="Filter…"
                        className="px-2 py-1 text-xs bg-carbon-white text-carbon-ink border border-carbon-gray-30 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60 w-36"
                        title="Filter rows (any column)"
                      />
                      {cpsc.enabled && (
                        <button
                          onClick={cpsc.refresh}
                          disabled={cpsc.loading}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50"
                          title="Refresh CPSC badges"
                        >
                          <RefreshCw size={12} className={cpsc.loading ? 'animate-spin' : ''} />
                          CPSC
                        </button>
                      )}
                      <button
                        onClick={handleExport}
                        disabled={!data?.detailData || data.detailData.length === 0}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Export to Excel"
                      >
                        <Download size={12} />
                        Export
                      </button>
                      </div>
                    </div>
                    <div className="overflow-auto max-h-96">
                      <table className="w-full text-xs">
                        <thead className="bg-carbon-white sticky top-0 z-10">
                          <tr>
                            {DETAIL_COLUMNS.map((col) => (
                              <th
                                key={col.field}
                                tabIndex={0}
                                aria-sort={detailSort.key === col.field ? (detailSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                onClick={() => toggleDetailSort(col.field)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDetailSort(col.field); } }}
                                title="Click to sort"
                                className={`${TH} whitespace-nowrap cursor-pointer select-none hover:bg-carbon-gray-10 ${col.align === 'right' ? 'text-right' : 'text-left'} ${detailSort.key === col.field ? 'text-carbon-ink font-medium' : ''}`}
                              >
                                {col.label}{detailSort.key === col.field ? (detailSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {displayRows.map((row) => (
                            <tr key={row._id} className={`border-b border-carbon-gray-10 ${isRowAttention(row, cpsc) ? 'bg-[#FFF1F1]' : 'hover:bg-carbon-gray-10'}`}>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.company}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap truncate max-w-[80px]">{row.division}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.customer}</td>
                              <td className={`px-2 py-1 ${MONO} text-carbon-ink whitespace-nowrap`}>{row.po}</td>
                              <td className={`px-2 py-1 ${MONO} text-carbon-ink whitespace-nowrap`}>
                                <span className="inline-flex items-center gap-1.5">{row.style}<CpscBadgeCell row={row} ctx={cpsc} onOverride={setOverrideStyle} /></span>
                              </td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap truncate max-w-[100px]" title={row.styleDesc}>{row.styleDesc}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.poManager}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.coo}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.shipTerms}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.stage}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.prodType}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.shipCode}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.paymentTerms}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap">{row.office}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap truncate max-w-[80px]">{row.lcBeneficiary}</td>
                              <td className="px-2 py-1 text-carbon-ink whitespace-nowrap truncate max-w-[80px]">{row.factory}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-ink whitespace-nowrap`}>{formatCurrency(row.totalSales)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.fob.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{formatCurrency(row.totalCost)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-ink whitespace-nowrap`}>{row.price.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} whitespace-nowrap`} style={{ color: '#24A148' }}>{formatCurrency(row.totalGp)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-ink whitespace-nowrap`}>{formatNumber(row.totalQty)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{formatNumber(row.cartonQty)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.freight.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.inlandFreight.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.tariff.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.tariffPct.toFixed(1)}%</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.dutyPct.toFixed(1)}%</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.duty.toFixed(2)}</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{row.royaltyPct.toFixed(1)}%</td>
                              <td className={`px-2 py-1 text-right ${MONO} text-carbon-gray-50 whitespace-nowrap`}>{formatCurrency(row.royaltyAmt)}</td>
                              <td className={`px-2 py-1 ${MONO} text-carbon-ink whitespace-nowrap`}>{row.dueDate ? new Date(row.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '-'}</td>
                              <td className={`px-2 py-1 ${MONO} text-carbon-ink whitespace-nowrap`}>{row.shipDate ? new Date(row.shipDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Verification Content */}
        {activeTab === 'verification' && (
          <div className="p-4">
            <VerificationContent verificationTab={verificationTab} userProfile={userProfile} />
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// VERIFICATION PANELS
// ============================================================================

const VerificationContent = ({ verificationTab, userProfile }) => {
  // CHA-282 Codex P2 — platform roles must pass tenantId explicitly (resolveTenant requires it for them);
  // tenant admins send nothing and resolve to their own tenant. Mirrors CpscBrokerHandoff.
  const isPlatformRole = userProfile?.role === 'platform_owner' || userProfile?.role === 'platform_admin';
  const tenantArg = isPlatformRole ? { tenantId: userProfile?.tenantId || 'inmocean' } : {};
  const [combinedSending, setCombinedSending] = useState(false);
  const [combinedStatus, setCombinedStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('inmocean');
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  // CHA-282 — the dedicated verification Google Sheet (read-only link + on-demand sync). The URL shown is
  // ALWAYS the server-sanitized stored one (rebuilt from the extracted spreadsheet id). CHA-289 — URL
  // EDITING moved to Admin → System Settings (the card here keeps a hint + the Sync-now action).
  const [sheetCfg, setSheetCfg] = useState(null);          // { sheetUrl, spreadsheetId } from the manage callable
  const [sheetSyncing, setSheetSyncing] = useState(false);
  const [sheetStatus, setSheetStatus] = useState(null);    // { message } — success line
  const [sheetError, setSheetError] = useState(null);      // server error message (ErrorBanner)
  const [lastSync, setLastSync] = useState(null);          // { counts, syncedAt } from the sync callable
  const [sheetCardHidden, setSheetCardHidden] = useState(false); // non-admin: config read denied → hide the card

  const DIVISION_OPTIONS = {
    inmocean: [
      { value: '10', label: '10' }, { value: '15', label: '15' },
      { value: '16', label: '16' }, { value: '42', label: '42' },
      { value: '45', label: '45' }, { value: '55', label: '55' },
      { value: '60', label: '60' }, { value: '61', label: '61' },
      { value: '77', label: '77' }, { value: '78', label: '78' },
    ],
    bwi: [
      { value: '30', label: '30' }, { value: '35', label: '35' }, { value: '38', label: '38' },
    ]
  };

  const FORMAT_LABELS = {
    inmocean: 'InMocean Operations',
    bwi: 'BWI Operations'
  };

  const toggleDiv = (v) => setSelectedDivisions(p => p.includes(v) ? p.filter(d => d !== v) : [...p, v]);
  const handleFormatChange = (fmt) => {
    setSelectedFormat(fmt);
    setSelectedDivisions(DIVISION_OPTIONS[fmt].map(d => d.value));
  };

  useEffect(() => {
    setSelectedDivisions(DIVISION_OPTIONS[selectedFormat].map(d => d.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CHA-282 — load the stored sheet config once (admin-gated callable; a denied/failed load surfaces in the card).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const manageCfg = httpsCallable(getFunctions(), 'manageProductionVerificationConfig');
        const res = await manageCfg({ op: 'get', ...tenantArg });
        if (!alive) return;
        setSheetCfg(res.data || null);
      } catch (err) {
        if (!alive) return;
        // Non-admin viewers can't read the (admin-gated) config — hide the admin-only card instead of
        // pinning a permanent error banner on a control they can never use (§6.3 finding 2).
        if (err?.code === 'functions/permission-denied') setSheetCardHidden(true);
        else setSheetError(err.message);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleSyncSheet = async () => {
    setSheetSyncing(true);
    setSheetStatus(null);
    setSheetError(null);
    try {
      const syncSheet = httpsCallable(getFunctions(), 'syncProductionVerificationSheet', { timeout: 180000 });
      const res = await syncSheet(tenantArg);
      setLastSync(res.data);
      // CHA-291 (review fold) — derive the per-tab counts from the server's tab list (title:rows),
      // never a hardcoded key list: a future registry check appears here with ZERO client changes.
      const tabLine = (res.data?.tabs || []).map((t) => `${t.title}: ${t.rows ?? 0}`).join(' · ');
      setSheetStatus({
        message: `Synced ${new Date(res.data?.syncedAt || Date.now()).toLocaleTimeString()}${tabLine ? ` — ${tabLine}` : ''}`,
      });
    } catch (err) {
      setSheetError(err.message);
    } finally {
      setSheetSyncing(false);
    }
  };

  const handleSendCombinedReports = async () => {
    if (!selectedDivisions.length) { setCombinedStatus({ type: 'warning', message: 'Select at least one division' }); return; }
    setCombinedSending(true);
    setCombinedStatus(null);
    try {
      const functions = getFunctions();
      const [ceResult, oiResult, osResult] = await Promise.all([
        httpsCallable(functions, 'getCostingErrorsData')(),
        httpsCallable(functions, 'getStaleIssuedOrders')(),
        httpsCallable(functions, 'getStaleShippedOrders')()
      ]);
      const filterByDiv = (rows) => rows.filter(r => selectedDivisions.includes(String(r.Division)));
      const costingErrors = filterByDiv(ceResult.data?.data || []);
      const openIssued = filterByDiv(oiResult.data?.data || []);
      const openShipped = filterByDiv(osResult.data?.data || []);
      if (!costingErrors.length && !openIssued.length && !openShipped.length) {
        setCombinedStatus({ type: 'warning', message: `No data for divisions ${selectedDivisions.join(', ')}` });
        setCombinedSending(false); return;
      }
      const payload = { format: selectedFormat, formatLabel: FORMAT_LABELS[selectedFormat], divisions: selectedDivisions };
      if (testMode && testEmail) {
        const sendCombined = httpsCallable(functions, 'sendCombinedProductionReport', { timeout: 330000 });
        await sendCombined({ managerCode: 'TEST', managerName: 'Test User', email: testEmail.trim(), costingErrors, openIssued, openShipped, ...payload });
        setCombinedStatus({ type: 'success', message: `${FORMAT_LABELS[selectedFormat]} test → ${testEmail} (CE:${costingErrors.length} OI:${openIssued.length} OS:${openShipped.length})` });
      } else {
        const db = getFirestore();
        const q = query(collection(db, 'email_distribution_list'), orderBy('name'));
        const snapshot = await getDocs(q);
        const emailList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const poManagers = emailList.filter(e => e.active && ['PO Manager', 'Operations', 'Executive'].includes(e.role));
        if (!poManagers.length) { setCombinedStatus({ type: 'warning', message: 'No active PO Managers found' }); setCombinedSending(false); return; }
        const groupBy = (rows) => { const m = {}; rows.forEach(r => { const k = r.Production_Manager || 'Unknown'; (m[k] = m[k] || []).push(r); }); return m; };
        const ceM = groupBy(costingErrors), oiM = groupBy(openIssued), osM = groupBy(openShipped);
        const sendCombined = httpsCallable(functions, 'sendCombinedProductionReport', { timeout: 330000 });
        let sent = 0, errs = [];
        for (const mgr of poManagers) {
          const ce = ceM[mgr.code] || [], oi = oiM[mgr.code] || [], os = osM[mgr.code] || [];
          if (ce.length + oi.length + os.length > 0) {
            try { await sendCombined({ managerCode: mgr.code, managerName: mgr.name, email: mgr.direct_email, costingErrors: ce, openIssued: oi, openShipped: os, ...payload }); sent++; }
            catch (e) { errs.push(mgr.name); }
          }
        }
        setCombinedStatus({ type: sent > 0 ? 'success' : 'warning', message: sent > 0 ? `Sent to ${sent} manager(s)${errs.length ? `. Failed: ${errs.join(', ')}` : ''}` : 'No matching data for any managers' });
      }
    } catch (err) {
      setCombinedStatus({ type: 'error', message: `Failed: ${err.message}` });
    } finally { setCombinedSending(false); }
  };

  // Cost Sheet Tester is a standalone ephemeral diagnostic (CHA-149) — it doesn't use
  // the shared division/email-report header or the costing-data panels, so render it
  // on its own (after all hooks, so hook order stays stable).
  if (verificationTab === 'cost-sheet-tester') {
    return <CostSheetTester />;
  }
  if (verificationTab === 'cpsc-test') {
    return <CpscComplianceWorklist userProfile={userProfile} />;
  }

  return (
    <>
      {/* CHA-282 — dedicated verification Google Sheet. CHA-289 — read-only link + Sync-now here; URL
          editing lives in Admin → System Settings (same manage callable, one write surface). */}
      {!sheetCardHidden && (
      <div className="mb-4 p-4 bg-carbon-white border border-carbon-border space-y-3 font-plex text-carbon-ink">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <FileSpreadsheet size={16} className="text-carbon-ink flex-shrink-0" />
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Google Sheet</span>
            {sheetCfg?.sheetUrl ? (
              <a href={sheetCfg.sheetUrl} target="_blank" rel="noreferrer" className="font-plex-mono text-[11px] text-carbon-blue-60 hover:text-carbon-blue-70 underline truncate">
                {sheetCfg.sheetUrl}
              </a>
            ) : (
              <span className="text-xs text-carbon-gray-50">No sheet configured</span>
            )}
            <span className="text-[11px] text-carbon-gray-50 whitespace-nowrap">Set in Admin → System Settings</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleSyncSheet}
              disabled={sheetSyncing}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
            >
              {sheetSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {sheetSyncing ? 'Syncing…' : 'Sync sheet now'}
            </button>
          </div>
        </div>
        {sheetStatus && (
          <div className="relative bg-carbon-white border border-carbon-border px-3 py-2 text-xs text-carbon-ink">
            <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-green-50" />
            {sheetStatus.message}
          </div>
        )}
        {sheetError && <ErrorBanner what="Verification sheet action failed." staleNote={sheetError} />}
        {lastSync && (
          <div className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-gray-50">
            Last sync {new Date(lastSync.syncedAt).toLocaleString()}{(lastSync.tabs || []).length ? ` — ${lastSync.tabs.map((t) => `${t.title} ${t.rows ?? 0}`).join(' · ')}` : ''}
          </div>
        )}
      </div>
      )}
      <div className="mb-4 p-4 bg-carbon-white border border-carbon-border space-y-3 font-plex text-carbon-ink">
        {/* Row 1: Format + Test + Send */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Mail size={16} className="text-carbon-ink" />
            <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Combined Report</span>
            <div className="flex border border-carbon-gray-30">
              <button onClick={() => handleFormatChange('inmocean')} className={`px-3 py-1 font-plex-mono text-[10.5px] uppercase font-medium ${selectedFormat === 'inmocean' ? 'bg-carbon-ink text-carbon-white' : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'}`}>InMocean Operations</button>
              <button onClick={() => handleFormatChange('bwi')} className={`px-3 py-1 font-plex-mono text-[10.5px] uppercase font-medium border-l border-carbon-gray-30 ${selectedFormat === 'bwi' ? 'bg-carbon-ink text-carbon-white' : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'}`}>BWI Operations</button>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} className="w-3.5 h-3.5 border-carbon-gray-30 text-carbon-blue-60" />
              <span className="text-xs text-carbon-gray-70">Test Mode</span>
            </label>
            {testMode && <input type="email" placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="px-2.5 py-1.5 text-xs border border-carbon-gray-30 bg-carbon-white text-carbon-ink w-48 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60" />}
            <button onClick={handleSendCombinedReports} disabled={combinedSending || (testMode && !testEmail) || !selectedDivisions.length} className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50">
              {combinedSending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {combinedSending ? 'Sending...' : testMode ? 'Send Test' : 'Send All Reports'}
            </button>
          </div>
        </div>
        {/* Row 2: Division Checkboxes */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-1">Divisions</span>
          <button onClick={() => setSelectedDivisions(DIVISION_OPTIONS[selectedFormat].map(d => d.value))} className="text-xs font-medium text-carbon-blue-60 hover:text-carbon-blue-70 underline">All</button>
          <button onClick={() => setSelectedDivisions([])} className="text-xs text-carbon-gray-40 hover:text-carbon-gray-70 underline">None</button>
          <span className="text-carbon-gray-30 mx-1">|</span>
          {DIVISION_OPTIONS[selectedFormat].map(div => (
            <label key={div.value} className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={selectedDivisions.includes(div.value)} onChange={() => toggleDiv(div.value)} className="w-3 h-3 border-carbon-gray-30 text-carbon-blue-60" />
              <span className="font-plex-mono text-[11px] text-carbon-gray-70">{div.label}</span>
            </label>
          ))}
        </div>
        {combinedStatus && (
          <div className="relative bg-carbon-white border border-carbon-border px-3 py-2 text-xs text-carbon-ink">
            <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${combinedStatus.type === 'success' ? 'bg-carbon-green-50' : combinedStatus.type === 'warning' ? 'bg-carbon-orange-40' : 'bg-carbon-red-60'}`} />
            {combinedStatus.message}
          </div>
        )}
      </div>
      {verificationTab === 'issues' && <IssuesPanel tenantArg={tenantArg} />}
      {verificationTab === 'rules' && <RulesPanel tenantArg={tenantArg} />}
      {verificationTab === 'tariff-rates' && <TariffRatesPanel tenantArg={tenantArg} />}
    </>
  );
};

// CHA-297 — Rules panel: the rule/check catalog served by the READ callable's `descriptors` field
// (RULE_DESCRIPTORS from the shared costSheetRules twins — constants interpolated server-side, so the
// panel always shows the DEPLOYED rule set, never a stale client copy — plus CHECK_DESCRIPTORS from
// productionVerification.js). Two groups: Term rules (R-A/R-B/R-C + the ruling scopes + the SP27
// cutoff) and Pipeline checks (one card per verification dataset). Severity chips reuse the CHA-289
// colors (invalid=red, warning=orange) + gray for info. Admin-gated callable — platform roles pass
// tenantId via tenantArg (CHA-282 Codex P2 pattern).
const SEVERITY_CHIP = {
  invalid: 'bg-carbon-red-60 text-carbon-white',
  warning: 'bg-carbon-orange-40 text-carbon-ink',
  info: 'bg-carbon-gray-20 text-carbon-gray-70',
};

const RulesPanel = ({ tenantArg }) => {
  const [descriptors, setDescriptors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const getData = httpsCallable(getFunctions(), 'getProductionVerificationData', { timeout: 180000 });
        const res = await getData(tenantArg);
        if (!alive) return;
        setDescriptors(res.data?.descriptors || { rules: [], checks: [] });
        setError(null);
      } catch (err) {
        console.error('Error fetching verification descriptors:', err);
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // tenantArg is derived from userProfile (stable per mount); reloadKey drives the explicit Retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="bg-carbon-white border border-carbon-border">
        <SkeletonRows rows={5} />
      </div>
    );
  }

  if (error) {
    return <ErrorBanner what="Verification rules failed to load." staleNote={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  const rules = descriptors?.rules || [];
  const checks = descriptors?.checks || [];

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Term rules — the shared costSheetRules twins (tester + pipeline judge with ONE implementation) */}
      <div>
        <div className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-gray-50 mb-2">
          Term rules ({rules.length})
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {rules.map((r) => (
            <div key={r.id} className="p-3 bg-carbon-white border border-carbon-border space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-plex-mono text-[11px] font-semibold">{r.id}</span>
                <span className={`inline-block px-2 py-0.5 font-plex-mono text-[10px] tracking-[0.04em] uppercase font-medium ${SEVERITY_CHIP[r.severity] || SEVERITY_CHIP.info}`}>
                  {r.severity}
                </span>
                {r.rulingDate && (
                  <span className="inline-block px-2 py-0.5 font-plex-mono text-[10px] tracking-[0.04em] uppercase font-medium border border-carbon-gray-30 text-carbon-gray-70">
                    Ruling {r.rulingDate}
                  </span>
                )}
                <span className="text-[13px] font-semibold">{r.name}</span>
              </div>
              <div className="text-[11.5px] text-carbon-gray-70">
                <span className="font-plex-mono text-[10px] tracking-[0.08em] uppercase text-carbon-gray-50 mr-1.5">Scope</span>
                {r.scope}
              </div>
              <div className="text-[12px] text-carbon-ink">{r.expectation}</div>
              {r.notes && <div className="text-[11px] text-carbon-gray-50">{r.notes}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Pipeline checks — one card per verification dataset (sheet tab / sub-tab) */}
      <div>
        <div className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold text-carbon-gray-50 mb-2">
          Pipeline checks ({checks.length})
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {checks.map((c) => (
            <div key={c.key} className="p-3 bg-carbon-white border border-carbon-border space-y-1.5">
              <div className="text-[13px] font-semibold">{c.title}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {(c.sources || []).map((s) => (
                  <span key={s} className="inline-block px-2 py-0.5 font-plex-mono text-[10px] border border-carbon-gray-30 text-carbon-gray-70">
                    {s}
                  </span>
                ))}
              </div>
              <div className="text-[12px] text-carbon-gray-70">{c.flags}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CHA-297 — the HTS duty/tariff reference table (Firestore lookup) is browsed/edited in the Admin
          Lookup Manager (plain-text pointer — same pattern as the sheet card's Settings hint). */}
      <div className="text-[11px] text-carbon-gray-50">
        HTS tariff reference table: Admin → Lookup Tables → HTS Tariff Reference (lookup_hts_tariff_reference)
      </div>
    </div>
  );
};


// CHA-295 — Tariff Rates panel: n316_TarPct by division × COO from BOTH enriched feeds, computed
// SERVER-SIDE by the shared verification engine (getProductionVerificationData — the same rows the
// sheet's 'Tariff Rates' tab gets, already sorted div → coo → source → rate). One row per
// (source, div, coo, ratePct) bucket; a cell carrying >1 distinct rate arrives with mixedRate stamped
// on every one of its rows (a 0 beside a real rate IS mixed), and a NULL rate is the missing-tariff
// bucket — rendered '—' with the same warning treatment, never dropped. Admin-gated callable —
// platform roles pass tenantId via tenantArg (CHA-282 Codex P2 pattern).
const TariffRatesPanel = ({ tenantArg }) => {
  const [rows, setRows] = useState([]);
  const [mixedCells, setMixedCells] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const getData = httpsCallable(getFunctions(), 'getProductionVerificationData', { timeout: 180000 });
        const res = await getData(tenantArg);
        if (!alive) return;
        setRows(res.data?.tariffRates || []);
        setMixedCells(res.data?.summary?.tariffMixedCells ?? 0);
        setError(null);
      } catch (err) {
        console.error('Error fetching tariff rates:', err);
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // tenantArg is derived from userProfile (stable per mount); reloadKey drives the explicit Retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="bg-carbon-white border border-carbon-border">
        <SkeletonRows rows={5} />
      </div>
    );
  }

  if (error) {
    return <ErrorBanner what="Tariff rates failed to load." staleNote={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  // A "cell" is one (source, div, coo) group — mixedCells counts these, so the summary counts them too
  // (a mixed cell spans several table rows, one per distinct rate; §6.3 review finding).
  const cellCount = new Set(rows.map((r) => JSON.stringify([r.source, r.div, r.coo]))).size;

  return (
    <div className="space-y-4 font-plex text-carbon-ink">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-gray-50">
          {cellCount.toLocaleString()} rate cells · {mixedCells.toLocaleString()} mixed
        </span>
        <span className="text-[11px] text-carbon-gray-50">
          n316_TarPct by division × COO, broken out by warehouse + type (CHA-342) · CPLAN → US warehouse only (operator rule 2026-07-14) · mixed = one feed × division × COO carries multiple rates (0 counts), the warehouse column shows which is odd · — = missing tariff
        </span>
      </div>
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-carbon-border bg-carbon-white">
        <table className="w-full text-xs">
          <thead className="bg-carbon-white sticky top-0">
            <tr>
              <th className={`${TH} text-left`}>Source</th>
              <th className={`${TH} text-left`}>Div</th>
              <th className={`${TH} text-left`}>COO</th>
              <th className={`${TH} text-left`}>Warehouse</th>
              <th className={`${TH} text-left`}>Type</th>
              <th className={`${TH} text-right`}>Rate %</th>
              <th className={`${TH} text-right`}>Rows</th>
              <th className={`${TH} text-right`}>Units</th>
              <th className={`${TH} text-left`}>Mixed</th>
              <th className={`${TH} text-left`}>Sample Styles</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const missingRate = row.ratePct == null; // missing tariff — warn like mixed
              const warn = row.mixedRate || missingRate;
              return (
                <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                  {/* Orange warning rail (banner-rail pattern) on mixed / missing-rate rows */}
                  <td className={`relative px-3 py-1.5 ${MONO} text-carbon-gray-70`}>
                    {warn && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-orange-40" />}
                    {row.source === 'open_wip' ? 'Open WIP' : 'Received'}
                  </td>
                  <td className={`px-3 py-1.5 ${MONO} text-carbon-ink`}>{row.div}</td>
                  <td className={`px-3 py-1.5 ${MONO} text-carbon-ink`}>{row.coo}</td>
                  <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70`}>{row.warehouse}</td>
                  <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70`}>{row.prodtype}</td>
                  <td className={`px-3 py-1.5 text-right ${MONO} ${missingRate ? 'font-semibold text-carbon-orange-70' : 'text-carbon-ink'}`}>
                    {missingRate ? '—' : `${row.ratePct}%`}
                  </td>
                  <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{Number(row.rows || 0).toLocaleString()}</td>
                  <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{Number(row.units || 0).toLocaleString()}</td>
                  <td className="px-3 py-1.5">
                    {row.mixedRate && (
                      <span className="inline-block px-2 py-0.5 font-plex-mono text-[10px] tracking-[0.04em] uppercase font-medium bg-carbon-orange-40 text-carbon-ink">
                        mixed
                      </span>
                    )}
                  </td>
                  <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70`}>{row.sampleStyles}</td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-carbon-gray-40 text-[13px]">No tariff rate rows found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};



// CHA-291 — the ISSUES-ONLY dashboard (the Verification landing view, operator-ratified: "only shows me
// the issues"). ONE read of the shared engine; every judgment is SERVER-side — which rows are issues
// (registry issueFilter, stamped issueSeverity) and each check's counts (res.issues) — so this view, the
// Google Sheet, and the phase-2 email can never disagree. Cards render ONLY checks with hits; each card's
// table derives its columns from the served descriptor headers (a future registry check needs ZERO client
// changes) via the canonical WorklistTable primitive (CHA-283). All-clear = the green queue-clear state.
const ISSUE_RAIL = { invalid: '#DA1E28', warning: '#FF832B', info: null };
const ISSUE_COUNT_CHIP = {
  invalid: 'bg-carbon-red-60 text-carbon-white',
  warning: 'bg-carbon-orange-40 text-carbon-ink',
  info: 'bg-carbon-gray-20 text-carbon-gray-70',
};
const IssuesPanel = ({ tenantArg }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [openKeys, setOpenKeys] = useState({}); // per-card expand state (first flagged card opens by default)

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const getData = httpsCallable(getFunctions(), 'getProductionVerificationData', { timeout: 180000 });
        const res = await getData(tenantArg);
        if (!alive) return;
        setData(res.data || null);
        setError(null);
      } catch (err) {
        console.error('Error fetching production issues:', err);
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // tenantArg is derived from userProfile (stable per mount); reloadKey drives the explicit Retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  if (loading) {
    return (
      <div className="bg-carbon-white border border-carbon-border">
        <SkeletonRows rows={6} />
      </div>
    );
  }

  if (error) {
    return <ErrorBanner what="Production issues failed to load." staleNote={error} onRetry={() => setReloadKey((k) => k + 1)} />;
  }

  // §6.3 Minor 3 — deploy-skew honesty: a response without the CHA-291 fields (old backend) must not
  // fabricate an all-clear. Render the recoverable banner instead.
  if (!data?.issues || !Array.isArray(data?.descriptors?.checks) || !data.descriptors.checks.length) {
    return <ErrorBanner what="Issues data unavailable." staleNote="The backend response carries no issue counts (mid-deploy?)." onRetry={() => setReloadKey((k) => k + 1)} />;
  }
  const checks = data.descriptors.checks;
  const issues = data.issues;
  // Issue rows carry the server's stamped issueSeverity — the filter here selects, never judges.
  const cards = checks
    .map((c) => ({
      ...c,
      counts: issues[c.key] || { total: 0, invalid: 0, warning: 0, info: 0 },
      // §6.3 Major 1 — datasets legitimately carry duplicate projections (live: 153 costing rows /
      // 38 distinct), so content-derived keys collide; stamp a positional id on COPIES for React keys.
      rows: (Array.isArray(data?.[c.key]) ? data[c.key] : [])
        .filter((r) => r && r.issueSeverity)
        .map((r, i) => ({ ...r, __issueRowId: `${c.key}:${i}` })),
    }))
    .filter((c) => c.counts.total > 0);
  const tot = cards.reduce(
    (a, c) => ({
      total: a.total + c.counts.total,
      invalid: a.invalid + c.counts.invalid,
      warning: a.warning + c.counts.warning,
      info: a.info + c.counts.info,
    }),
    { total: 0, invalid: 0, warning: 0, info: 0 }
  );
  const isOpen = (key, idx) => (openKeys[key] !== undefined ? openKeys[key] : idx === 0);
  const toggle = (key, idx) => setOpenKeys((p) => ({ ...p, [key]: !isOpen(key, idx) }));

  const KPI = [
    { label: 'Total Issues', value: tot.total, cls: 'text-carbon-ink' },
    { label: 'Invalid', value: tot.invalid, cls: tot.invalid ? 'text-carbon-red-60' : 'text-carbon-gray-40' },
    { label: 'Warning', value: tot.warning, cls: tot.warning ? 'text-carbon-orange-70' : 'text-carbon-gray-40' },
    { label: 'Info', value: tot.info, cls: 'text-carbon-gray-70' },
    { label: 'Checks Flagging', value: `${cards.length} / ${checks.length}`, cls: 'text-carbon-ink' },
  ];

  return (
    <div className="space-y-4 font-plex text-carbon-ink">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-carbon-border border border-carbon-border">
        {KPI.map((k) => (
          <div key={k.label} className="bg-carbon-white px-4 py-3">
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">{k.label}</div>
            <div className={`text-xl font-semibold mt-1 ${k.cls}`}>{typeof k.value === 'number' ? k.value.toLocaleString() : k.value}</div>
          </div>
        ))}
      </div>

      {/* CHA-297 fail-safe accounting (§6.3 Major 2 — must survive the panel consolidation): unlisted
          CN HTS groups are NOT checked, so their count renders whether or not any card is flagging —
          zero mismatch rows must never read as all-clear while most CN codes aren't in the reference. */}
      {(data?.htsReferenceStats?.unlistedLines ?? 0) > 0 && (
        <div className="bg-carbon-white border border-carbon-border px-4 py-2 text-[11px] text-carbon-gray-70">
          <span className="font-plex-mono text-[10px] tracking-[0.08em] uppercase text-carbon-gray-50 mr-2">HTS reference coverage</span>
          {data.htsReferenceStats.unlistedLines.toLocaleString()} CPLAN CN cost-sheet lines are not in the reference — NOT checked. Add codes in Admin → Lookups → HTS Tariff Reference.
        </div>
      )}
      {/* CHA-304 (review fold) — same fail-safe doctrine as the HTS note: unlinked programs are NOT
          checked, so their count renders regardless of card state (74% of live groups at ship time). */}
      {(data?.royaltyStats?.unlinkedGroups ?? 0) > 0 && (
        <div className="bg-carbon-white border border-carbon-border px-4 py-2 text-[11px] text-carbon-gray-70">
          <span className="font-plex-mono text-[10px] tracking-[0.08em] uppercase text-carbon-gray-50 mr-2">Royalty linking coverage</span>
          {data.royaltyStats.unlinkedGroups.toLocaleString()} ERP program groups ({data.royaltyStats.unlinkedRows.toLocaleString()} rows) have no brand link — NOT checked. Link programs in Admin → Lookups → Royalty Rates.
        </div>
      )}

      {!cards.length && (
        <div className="bg-carbon-white border border-carbon-border">
          <EmptyGoodNews title="All checks clear." detail={`All ${checks.length} verification checks ran with zero issues.`} />
        </div>
      )}

      {cards.map((c, idx) => (
        <div key={c.key} className="bg-carbon-white border border-carbon-border">
          <button
            type="button"
            onClick={() => toggle(c.key, idx)}
            aria-expanded={isOpen(c.key, idx)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-carbon-gray-10"
          >
            <span className={`inline-block w-[3px] self-stretch ${c.counts.invalid ? 'bg-carbon-red-60' : c.counts.warning ? 'bg-carbon-orange-40' : 'bg-carbon-gray-30'}`} />
            <span className="text-[13.5px] font-semibold flex-1">{c.title}</span>
            {['invalid', 'warning', 'info'].map((sev) => (c.counts[sev] ? (
              <span key={sev} className={`px-2 py-0.5 font-plex-mono text-[10px] tracking-[0.04em] uppercase font-medium ${ISSUE_COUNT_CHIP[sev]}`}>
                {c.counts[sev].toLocaleString()} {sev}
              </span>
            ) : null))}
            <span className="font-plex-mono text-[10.5px] text-carbon-gray-50">{isOpen(c.key, idx) ? '−' : '+'}</span>
          </button>
          {isOpen(c.key, idx) && (
            <WorklistTable
              columns={(c.headers || []).map((h) => ({
                key: h,
                label: h,
                render: (r) => (
                  <span className="font-plex-mono text-[11px] text-carbon-ink">
                    {r[h] == null ? '' : typeof r[h] === 'boolean' ? (r[h] ? 'yes' : '') : String(r[h])}
                  </span>
                ),
              }))}
              rows={c.rows}
              rowKey={(r) => r.__issueRowId}
              severityOf={(r) => ISSUE_RAIL[r.issueSeverity] || null}
              pagination={c.rows.length > 25 ? { pageSize: 25 } : undefined}
              minWidth={c.headers.length > 8 ? '1080px' : undefined}
              empty={{ title: 'Check clear.' }}
            />
          )}
        </div>
      ))}
    </div>
  );
};
